import React, { useState, useEffect, useCallback } from 'react';
import { 
  X, 
  User, 
  Building, 
  Mail, 
  Phone,
  Calendar,
  Activity,
  ChevronRight,
  ArrowLeft,
  Search
} from 'lucide-react';
import { 
  fetchPersonOrganizations, 
  fetchOrganizationAssessors,
  fetchPersonOrgSessions 
} from '../services/adminService';
import { AdminUser, AdminOrganization, AdminSession } from '../types/admin.types';
import SessionList from './SessionList';

interface EntityDetailProps {
  entity: AdminUser | AdminOrganization | null;
  entityType: 'person' | 'organization' | null;
  onClose: () => void;
  onSelectPerson?: (email: string, name: string) => void;
  onSelectOrganization?: (name: string) => void;
  onSelectSession?: (session: AdminSession) => void;
}

const EntityDetail: React.FC<EntityDetailProps> = ({ 
  entity, 
  entityType,
  onClose,
  onSelectPerson,
  onSelectOrganization,
  onSelectSession
}) => {
  const [relatedData, setRelatedData] = useState<any[]>([]);
  const [sessions, setSessions] = useState<AdminSession[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedRelated, setSelectedRelated] = useState<any>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState<'overview' | 'sessions'>('overview');

  const loadRelatedData = useCallback(async () => {
    if (!entity || !entityType) return;
    
    setLoading(true);
    try {
      if (entityType === 'person') {
        const user = entity as AdminUser;
        const orgs = await fetchPersonOrganizations(user.email);
        setRelatedData(orgs.map(org => ({ name: org, type: 'organization' })));
      } else {
        const org = entity as AdminOrganization;
        const assessors = await fetchOrganizationAssessors(org.name);
        setRelatedData(assessors.map(a => ({ ...a, type: 'person' })));
      }
    } catch (error) {
    }
    setLoading(false);
  }, [entity, entityType]);

  useEffect(() => {
    if (entity && entityType) {
      loadRelatedData();
      setSelectedRelated(null);
      setSessions([]);
      setViewMode('overview');
    }
  }, [entity, entityType, loadRelatedData]);

  const loadSessions = async (related: any) => {
    setLoading(true);
    try {
      let sessionsData: AdminSession[] = [];
      
      if (entityType === 'person') {
        const user = entity as AdminUser;
        sessionsData = await fetchPersonOrgSessions(user.email, related.name);
      } else {
        const org = entity as AdminOrganization;
        sessionsData = await fetchPersonOrgSessions(related.email, org.name);
      }
      
      setSessions(sessionsData);
      setSelectedRelated(related);
      setViewMode('sessions');
    } catch (error) {
    }
    setLoading(false);
  };

  const formatDateTime = (timestamp: any): string => {
    if (!timestamp) return '-';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  const filteredRelatedData = relatedData.filter(item => {
    if (!searchTerm) return true;
    const searchLower = searchTerm.toLowerCase();
    if (item.type === 'organization') {
      return item.name.toLowerCase().includes(searchLower);
    } else {
      return item.name?.toLowerCase().includes(searchLower) || 
             item.email?.toLowerCase().includes(searchLower);
    }
  });

  if (!entity || !entityType) return null;

  const isPerson = entityType === 'person';
  const user = isPerson ? entity as AdminUser : null;
  const org = !isPerson ? entity as AdminOrganization : null;

  return (
    <div className="fixed inset-y-0 right-0 w-full max-w-2xl bg-white shadow-xl z-50 flex flex-col">
      {/* Header */}
      <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            {viewMode === 'sessions' && (
              <button
                onClick={() => {
                  setViewMode('overview');
                  setSelectedRelated(null);
                }}
                className="p-1 hover:bg-gray-200 rounded-lg transition-colors"
              >
                <ArrowLeft className="h-5 w-5 text-gray-600" />
              </button>
            )}
            {isPerson ? (
              <User className="h-5 w-5 text-gray-500" />
            ) : (
              <Building className="h-5 w-5 text-gray-500" />
            )}
            <div>
              <h2 className="text-lg font-semibold text-gray-900">
                {isPerson ? (user?.name || user?.email) : org?.name}
              </h2>
              {viewMode === 'sessions' && selectedRelated && (
                <p className="text-sm text-gray-500">
                  {isPerson 
                    ? `Sessions at ${selectedRelated.name}`
                    : `Sessions by ${selectedRelated.name || selectedRelated.email}`
                  }
                </p>
              )}
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 hover:bg-gray-200 rounded-lg transition-colors"
          >
            <X className="h-5 w-5 text-gray-500" />
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto">
        {viewMode === 'overview' ? (
          <>
            {/* Entity Details */}
            <div className="px-6 py-4 bg-white">
              {isPerson && user ? (
                <div className="space-y-3">
                  <div className="flex items-center gap-2 text-sm">
                    <Mail className="h-4 w-4 text-gray-400" />
                    <span className="text-gray-900">{user.email}</span>
                  </div>
                  {user.phone && (
                    <div className="flex items-center gap-2 text-sm">
                      <Phone className="h-4 w-4 text-gray-400" />
                      <span className="text-gray-900">{user.phone}</span>
                    </div>
                  )}
                  {user.organizationName && (
                    <div className="flex items-center gap-2 text-sm">
                      <Building className="h-4 w-4 text-gray-400" />
                      <span className="text-gray-900">{user.organizationName}</span>
                    </div>
                  )}
                  <div className="flex items-center gap-2 text-sm">
                    <Calendar className="h-4 w-4 text-gray-400" />
                    <span className="text-gray-900">
                      First seen: {formatDateTime(user.firstSeen)}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <Activity className="h-4 w-4 text-gray-400" />
                    <span className="text-gray-900">
                      {user.totalSessions} total sessions
                    </span>
                  </div>
                  {user.isRooster && (
                    <span className="inline-block bg-purple-100 text-purple-700 text-sm px-3 py-1 rounded-full">
                      Rooster
                    </span>
                  )}
                </div>
              ) : org && (
                <div className="space-y-3">
                  {org.industry && (
                    <div className="text-sm">
                      <span className="text-gray-500">Industry:</span>
                      <span className="ml-2 text-gray-900">{org.industry}</span>
                    </div>
                  )}
                  {org.companySize && (
                    <div className="text-sm">
                      <span className="text-gray-500">Company Size:</span>
                      <span className="ml-2 text-gray-900">{org.companySize}</span>
                    </div>
                  )}
                  {org.totalEmployees && (
                    <div className="text-sm">
                      <span className="text-gray-500">Total Employees:</span>
                      <span className="ml-2 text-gray-900">{org.totalEmployees.toLocaleString()}</span>
                    </div>
                  )}
                  <div className="text-sm">
                    <span className="text-gray-500">Total Assessments:</span>
                    <span className="ml-2 text-gray-900">{org.totalAssessments}</span>
                  </div>
                  {org.latestTASScore && (
                    <div className="text-sm">
                      <span className="text-gray-500">Latest TAS Score:</span>
                      <span className="ml-2 text-gray-900 font-medium">
                        {Math.round(org.latestTASScore)}
                      </span>
                    </div>
                  )}
                  <div className="text-sm">
                    <span className="text-gray-500">First Assessment:</span>
                    <span className="ml-2 text-gray-900">{formatDateTime(org.firstAssessment)}</span>
                  </div>
                  <div className="text-sm">
                    <span className="text-gray-500">Last Assessment:</span>
                    <span className="ml-2 text-gray-900">{formatDateTime(org.lastAssessment)}</span>
                  </div>
                </div>
              )}
            </div>

            {/* Related Data Section */}
            <div className="px-6 py-4 border-t border-gray-200">
              <div className="mb-4">
                <h3 className="text-sm font-semibold text-gray-900 mb-2">
                  {isPerson ? 'Organizations Assessed' : 'Assessors'}
                </h3>
                {relatedData.length > 5 && (
                  <div className="relative mb-3">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <input
                      type="text"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      placeholder={isPerson ? "Search organizations..." : "Search assessors..."}
                      className="w-full pl-9 pr-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-sky-500 focus:border-sky-500"
                    />
                  </div>
                )}
              </div>

              {loading ? (
                <div className="flex justify-center py-8">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-sky-600"></div>
                </div>
              ) : filteredRelatedData.length === 0 ? (
                <p className="text-sm text-gray-500 text-center py-4">
                  {searchTerm ? 'No results found' : `No ${isPerson ? 'organizations' : 'assessors'} found`}
                </p>
              ) : (
                <div className="space-y-2">
                  {filteredRelatedData.map((item, index) => (
                    <button
                      key={index}
                      onClick={() => {
                        // When viewing an org and clicking an assessor, just load their sessions
                        // Don't switch to the person's detail view
                        if (entityType === 'organization' && item.type === 'person') {
                          // Just load sessions, don't call onSelectPerson
                          loadSessions(item);
                        } 
                        // When viewing a person and clicking an org, switch to that org
                        else if (entityType === 'person' && item.type === 'organization' && onSelectOrganization) {
                          onSelectOrganization(item.name);
                          loadSessions(item);
                        }
                      }}
                      className="w-full p-3 bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors flex items-center justify-between group"
                    >
                      <div className="flex items-center gap-3">
                        {item.type === 'organization' ? (
                          <Building className="h-5 w-5 text-gray-400" />
                        ) : (
                          <User className="h-5 w-5 text-gray-400" />
                        )}
                        <div className="text-left">
                          <div className="font-medium text-gray-900">
                            {item.name || item.email}
                          </div>
                          {item.type === 'person' && item.email && (
                            <div className="text-xs text-gray-500">{item.email}</div>
                          )}
                          {item.sessionCount !== undefined && (
                            <div className="text-xs text-gray-500 mt-0.5">
                              {item.sessionCount} session{item.sessionCount !== 1 ? 's' : ''}
                            </div>
                          )}
                        </div>
                      </div>
                      <ChevronRight className="h-4 w-4 text-gray-400 group-hover:text-gray-600" />
                    </button>
                  ))}
                </div>
              )}
            </div>
          </>
        ) : (
          /* Sessions View */
          <SessionList 
            sessions={sessions}
            loading={loading}
            title={`Sessions (${sessions.length})`}
            onSelectSession={onSelectSession}
          />
        )}
      </div>
    </div>
  );
};

export default EntityDetail;